import type { Contact } from '../api/types';
import type { Suggestion } from './recents';

/*
    Who the composer can suggest.

    Until there was an address book this could only be the addresses this browser had written to
    before — recents.ts explains why, and the reason was true: IMAP knows about mailboxes, not
    people. A CardDAV server is exactly the directory that was missing, so the two are put
    together rather than one replacing the other.

    Neither source is enough alone. The address book holds people somebody deliberately wrote
    down, including ones they have never mailed; the local history holds everyone they actually
    correspond with, including the ones nobody ever gets around to saving.
*/

/** A contact counts as one entry per address: people are reached at an address, not at a card. */
export function fromContacts(contacts: Contact[]): Suggestion[] {
    const out: Suggestion[] = [];
    for (const contact of contacts) {
        for (const email of contact.email) {
            if (!email.trim()) continue;
            out.push({
                email,
                name: contact.name || null,
                /*
                    Zero, and that is the point. `suggest` ranks on the match first and falls back
                    to how often an address has been used, so a person somebody actually writes to
                    comes before a card that merely mentions the same thing — while a contact
                    nobody has mailed still appears, because the match is what decides.
                */
                count: 0,
                at: 0,
            });
        }
    }
    return out;
}

/**
 * Merges the two, letting the local history win on any address they share.
 *
 * The history's entry carries a usage count and a last-seen time, which is what the ranking is
 * built on; the book's entry has neither. Keeping the book's copy instead would quietly flatten
 * everyone's suggestions to alphabetical.
 */
export function merge(local: Suggestion[], fromBook: Suggestion[]): Suggestion[] {
    const seen = new Set(local.map((s) => s.email.toLowerCase()));
    const out = [...local];
    for (const s of fromBook) {
        const key = s.email.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(s);
    }
    return out;
}
